import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Save, ExpandMore, ExpandLess } from "@mui/icons-material";
import { getPortalRegistry } from "services/operatorService";

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
  },
  sectionTitle: {
    fontWeight: "600 !important",
    color: "#333333",
    marginBottom: `${theme.spacing(1)} !important`,
    borderBottom: "2px solid #f0f0f0",
    paddingBottom: `${theme.spacing(1)} !important`,
  },
  sectionSubtitle: {
    marginBottom: `${theme.spacing(3)} !important`,
    color: "#666666",
  },
  pageBlock: {
    border: "1px solid #e8e8e8",
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    overflow: "hidden",
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
    backgroundColor: "#f8f8f8",
    cursor: "pointer",
    userSelect: "none",
  },
  pageBody: {
    padding: theme.spacing(2),
  },
  rowToggle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  subSection: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid #f0f0f0",
  },
  subSectionTitle: {
    fontWeight: "600 !important",
    color: "#555555",
    marginBottom: `${theme.spacing(1)} !important`,
  },
  columnRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
  },
  sectionSaveButton: {
    marginTop: `${theme.spacing(2)} !important`,
    backgroundColor: "#4caf50 !important",
    color: "white !important",
    fontWeight: "bold !important",
    "&:hover": {
      backgroundColor: "#45a049 !important",
    },
    "&:disabled": {
      backgroundColor: "#e0e0e0 !important",
      color: "#9e9e9e !important",
    },
  },
}));

const PortalConfigSection = ({
  portalConfig,
  onChange,
  onSave,
  loading,
  hideSaveButton = false,
}) => {
  const styles = useStyles();
  const [localConfig, setLocalConfig] = useState(portalConfig || {});
  const [expandedPages, setExpandedPages] = useState({});
  const [registry, setRegistry] = useState<any[]>([]);
  const [registryLoading, setRegistryLoading] = useState(true);

  useEffect(() => {
    setLocalConfig(portalConfig || {});
  }, [portalConfig]);

  useEffect(() => {
    getPortalRegistry()
      .then((resp) => setRegistry(resp?.data ?? []))
      .catch(() => setRegistry([]))
      .finally(() => setRegistryLoading(false));
  }, []);

  const emitChange = (updatedConfig) => {
    onChange({ target: { name: "portalConfig", value: updatedConfig } });
  };

  // Immutably sets a value at a nested path, creating intermediate objects as needed.
  // Spreading undefined/null is safe in JS and yields an empty object.
  const setIn = (obj: any, path: string[], value: any): any => {
    if (path.length === 0) return value;
    const [key, ...rest] = path;
    return { ...obj, [key]: setIn(obj?.[key], rest, value) };
  };

  const updateConfig = (path: string[], value: any) => {
    const updated = setIn(localConfig, path, value);
    setLocalConfig(updated);
    emitChange(updated);
  };

  const setMaxDateRange = (value) =>
    updateConfig(
      ["maxDateRangeDays"],
      value === "" ? undefined : Number(value),
    );

  const setInfoPageContent = (value) =>
    updateConfig(["infoPageContent"], value);

  const setPageEnabled = (pageId, enabled) =>
    updateConfig(["pages", pageId, "enabled"], enabled);

  const setPageNavLabel = (pageId, navLabel) =>
    updateConfig(["pages", pageId, "navLabel"], navLabel);

  const setStatCard = (pageId, cardId, field, value) =>
    updateConfig(["pages", pageId, "statCards", cardId, field], value);

  const setTableTitle = (pageId, tableId, title) =>
    updateConfig(["pages", pageId, "tables", tableId, "title"], title);

  const setColumn = (pageId, tableId, colId, field, value) =>
    updateConfig(
      ["pages", pageId, "tables", tableId, "columns", colId, field],
      value,
    );

  const setSectionEnabled = (pageId, sectionId, enabled) =>
    updateConfig(["pages", pageId, "sections", sectionId, "enabled"], enabled);

  const setField = (pageId, sectionId, fieldId, key, value) =>
    updateConfig(
      ["pages", pageId, "sections", sectionId, "fields", fieldId, key],
      value,
    );

  const togglePageExpanded = (pageId) => {
    setExpandedPages((prev) => ({ ...prev, [pageId]: !prev[pageId] }));
  };

  return (
    <Paper elevation={1} className={styles.paperContainer}>
      <Box>
        <Typography variant="h6" className={styles.sectionTitle}>
          Portal Configuration
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          className={styles.sectionSubtitle}
        >
          Customize page access, table columns, column labels, and stat card
          visibility for this operator's reporting portal.
        </Typography>
      </Box>

      {/* Max Date Range */}
      <Box mb={3}>
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 600, marginBottom: 8 }}
        >
          Max Date Range (days)
        </Typography>
        <TextField
          type="number"
          size="small"
          variant="outlined"
          value={localConfig.maxDateRangeDays ?? 14}
          onChange={(e) => setMaxDateRange(e.target.value)}
          inputProps={{ min: 1, max: 365 }}
          style={{ width: 120 }}
        />
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginTop: 4 }}
        >
          Maximum number of days the operator can query in date range filters
          (1-365).
        </Typography>
      </Box>

      <Divider style={{ marginBottom: 16 }} />

      <Typography
        variant="subtitle1"
        style={{ fontWeight: 600, marginBottom: 12 }}
      >
        Pages
      </Typography>

      {registryLoading ? (
        <Box display="flex" alignItems="center" gap={1} py={2}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary">
            Loading portal registry...
          </Typography>
        </Box>
      ) : registry.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No pages found in portal registry.
        </Typography>
      ) : (
        registry.map((pageDef) => {
          const pageOverride = localConfig.pages?.[pageDef.id] ?? {};
          const isEnabled = pageOverride.enabled !== false;
          const isExpanded = !!expandedPages[pageDef.id];

          return (
            <Box key={pageDef.id} className={styles.pageBlock}>
              <Box
                className={styles.pageHeader}
                onClick={() => togglePageExpanded(pageDef.id)}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {isExpanded ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    {pageDef.defaultNavLabel}
                  </Typography>
                  {!isEnabled && (
                    <Typography
                      variant="caption"
                      style={{ color: "#f44336", marginLeft: 8 }}
                    >
                      (disabled)
                    </Typography>
                  )}
                </Box>
              </Box>

              {isExpanded && (
                <Box className={styles.pageBody}>
                  {/* Enable/Disable page */}
                  <Box className={styles.rowToggle}>
                    <Typography variant="body2">Enable this page</Typography>
                    <Switch
                      checked={isEnabled}
                      onChange={(e) =>
                        setPageEnabled(pageDef.id, e.target.checked)
                      }
                      size="small"
                    />
                  </Box>

                  {/* Custom nav label */}
                  <Box mb={2}>
                    <TextField
                      label="Nav Label"
                      size="small"
                      variant="outlined"
                      fullWidth
                      placeholder={pageDef.defaultNavLabel}
                      value={pageOverride.navLabel ?? ""}
                      onChange={(e) =>
                        setPageNavLabel(pageDef.id, e.target.value)
                      }
                      helperText={`Default: "${pageDef.defaultNavLabel}"`}
                    />
                  </Box>

                  {/* Stat Cards */}
                  {pageDef.statCards && pageDef.statCards.length > 0 && (
                    <Box className={styles.subSection}>
                      <Typography
                        variant="body2"
                        className={styles.subSectionTitle}
                      >
                        Stat Cards
                      </Typography>
                      {pageDef.statCards.map((card) => {
                        const cardOverride =
                          pageOverride.statCards?.[card.id] ?? {};
                        return (
                          <Box key={card.id} className={styles.columnRow}>
                            <Switch
                              checked={cardOverride.visible !== false}
                              onChange={(e) =>
                                setStatCard(
                                  pageDef.id,
                                  card.id,
                                  "visible",
                                  e.target.checked,
                                )
                              }
                              size="small"
                            />
                            <Typography
                              variant="body2"
                              style={{ minWidth: 120 }}
                            >
                              {card.defaultLabel}
                            </Typography>
                            <TextField
                              size="small"
                              variant="outlined"
                              placeholder={`Custom label (default: "${card.defaultLabel}")`}
                              value={cardOverride.label ?? ""}
                              onChange={(e) =>
                                setStatCard(
                                  pageDef.id,
                                  card.id,
                                  "label",
                                  e.target.value,
                                )
                              }
                              style={{ flex: 1 }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {/* Sections (e.g. Transaction Details fields) */}
                  {pageDef.sections && pageDef.sections.length > 0 && (
                    <Box className={styles.subSection}>
                      <Typography
                        variant="body2"
                        className={styles.subSectionTitle}
                      >
                        Detail Sections
                      </Typography>
                      {pageDef.sections.map((sectionDef) => {
                        const sectionOverride =
                          pageOverride.sections?.[sectionDef.id] ?? {};
                        const sectionEnabled =
                          sectionOverride.enabled !== false;
                        return (
                          <Box
                            key={sectionDef.id}
                            style={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 6,
                              marginBottom: 12,
                              overflow: "hidden",
                            }}
                          >
                            {/* Section header with enable toggle */}
                            <Box
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "8px 12px",
                                backgroundColor: "#f5f5f5",
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              <Typography
                                variant="caption"
                                style={{
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  color: sectionEnabled ? "#333" : "#aaa",
                                }}
                              >
                                {sectionDef.defaultTitle}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  Show section
                                </Typography>
                                <Switch
                                  checked={sectionEnabled}
                                  onChange={(e) =>
                                    setSectionEnabled(
                                      pageDef.id,
                                      sectionDef.id,
                                      e.target.checked,
                                    )
                                  }
                                  size="small"
                                />
                              </Box>
                            </Box>

                            {/* Fields list */}
                            <Box style={{ padding: "8px 12px" }}>
                              {sectionDef.fields.map((fieldDef) => {
                                const fieldOverride =
                                  sectionOverride.fields?.[fieldDef.id] ?? {};
                                return (
                                  <Box
                                    key={fieldDef.id}
                                    className={styles.columnRow}
                                  >
                                    <Switch
                                      checked={fieldOverride.visible !== false}
                                      onChange={(e) =>
                                        setField(
                                          pageDef.id,
                                          sectionDef.id,
                                          fieldDef.id,
                                          "visible",
                                          e.target.checked,
                                        )
                                      }
                                      size="small"
                                      disabled={!sectionEnabled}
                                    />
                                    <Typography
                                      variant="body2"
                                      style={{
                                        minWidth: 160,
                                        color: sectionEnabled
                                          ? "inherit"
                                          : "#aaa",
                                      }}
                                    >
                                      {fieldDef.defaultLabel}
                                    </Typography>
                                    <TextField
                                      size="small"
                                      variant="outlined"
                                      placeholder={`Custom label (default: "${fieldDef.defaultLabel}")`}
                                      value={fieldOverride.label ?? ""}
                                      onChange={(e) =>
                                        setField(
                                          pageDef.id,
                                          sectionDef.id,
                                          fieldDef.id,
                                          "label",
                                          e.target.value,
                                        )
                                      }
                                      style={{ flex: 1 }}
                                      disabled={!sectionEnabled}
                                    />
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {/* Tables */}
                  {pageDef.tables.map((tableDef) => {
                    const tableOverride =
                      pageOverride.tables?.[tableDef.id] ?? {};
                    return (
                      <Box key={tableDef.id} className={styles.subSection}>
                        <Typography
                          variant="body2"
                          className={styles.subSectionTitle}
                        >
                          Table: {tableDef.defaultTitle}
                        </Typography>

                        <Box mb={2}>
                          <TextField
                            label="Table Title"
                            size="small"
                            variant="outlined"
                            fullWidth
                            placeholder={tableDef.defaultTitle}
                            value={tableOverride.title ?? ""}
                            onChange={(e) =>
                              setTableTitle(
                                pageDef.id,
                                tableDef.id,
                                e.target.value,
                              )
                            }
                            helperText={`Default: "${tableDef.defaultTitle}"`}
                          />
                        </Box>

                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ display: "block", marginBottom: 8 }}
                        >
                          Columns
                        </Typography>
                        {tableDef.columns.map((col) => {
                          const colOverride =
                            tableOverride.columns?.[col.id] ?? {};
                          return (
                            <Box key={col.id} className={styles.columnRow}>
                              <Switch
                                checked={colOverride.visible !== false}
                                onChange={(e) =>
                                  setColumn(
                                    pageDef.id,
                                    tableDef.id,
                                    col.id,
                                    "visible",
                                    e.target.checked,
                                  )
                                }
                                size="small"
                              />
                              <Typography
                                variant="body2"
                                style={{ minWidth: 120 }}
                              >
                                {col.defaultLabel}
                              </Typography>
                              <TextField
                                size="small"
                                variant="outlined"
                                placeholder={`Custom label (default: "${col.defaultLabel}")`}
                                value={colOverride.label ?? ""}
                                onChange={(e) =>
                                  setColumn(
                                    pageDef.id,
                                    tableDef.id,
                                    col.id,
                                    "label",
                                    e.target.value,
                                  )
                                }
                                style={{ flex: 1 }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })
      )}

      <Divider style={{ marginBottom: 16 }} />

      {/* Info Page Content */}
      <Box mb={3}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          Portal Info Page Content (Markdown)
        </Typography>
        <TextField
          multiline
          minRows={6}
          maxRows={20}
          size="small"
          variant="outlined"
          fullWidth
          value={localConfig.infoPageContent ?? ""}
          onChange={(e) => setInfoPageContent(e.target.value)}
          placeholder={
            "# Welcome\n\nEnter markdown content to display on the Information page."
          }
          inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
        />
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginTop: 4 }}
        >
          Markdown text shown on the portal Information page. Enable the
          Information page below to display it.
        </Typography>
      </Box>

      {!hideSaveButton && (
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={onSave}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
            className={styles.sectionSaveButton}
          >
            {loading ? "Saving..." : "Save Portal Config"}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default PortalConfigSection;
